import { HttpClient } from 'api/http-client';
import { ApiTaskStatusesWithCount } from 'api/sprint-report/types';
import { Task } from 'api/tasks';
import { QueryCombinerType } from 'api/tasks/filter-types';
import { PaginatedResponse } from 'api/types';
import {
  CompleteSprintPayload,
  EpicUpdatePayload,
  EpicRemoveTaskPayload,
  SprintInfoPayload,
  SprintRemoveTaskPayload,
  SprintUpdatePayload,
  ApiBacklogSprintList,
  EpicGroupByFilter,
} from 'components/organisms/sprint-planning/types';
import { fieldAccessMetaToBase3 } from 'utils/field-access';
import { constructUrl } from 'utils/http-utilts';
import { getSimpleSearchFilterModule } from 'utils/simple-search-filter/module';
import {
  GetSprintListPayload,
  PaginatedSprintParams,
  PaginatedSprintResponse,
  Sprint,
  SprintConfig,
  SprintsList,
  TasksInSprintForExternalUser,
  PaginatedEpicParams,
  PaginatedEpicResponse,
  Epic,
  Backlog,
  PaginatedBacklogResponse,
  PaginatedBacklogParams,
  PaginatedBacklogTasksParams,
  PaginatedSprintBacklogTasksParams,
  CheckTaskMovabilityResponse,
  PaginatedGroupedBacklogTasksParams,
} from './types';

type PaginatedTaskResponse = PaginatedResponse<Task>;

class SprintPlanningHttpClient extends HttpClient {
  public async getSprintConfiguration(): Promise<SprintConfig> {
    return this.get('/sprint-planning/configuration');
  }

  public async updateConfig(newConfig: SprintConfig): Promise<SprintConfig> {
    return this.put('/sprint-planning/configuration', newConfig);
  }

  public async createSprint(payload: SprintInfoPayload): Promise<Sprint> {
    return this.post('/sprints', payload);
  }

  public async getSprint(sprintId: number): Promise<Sprint> {
    return this.get(`/sprints/${sprintId}`);
  }

  public async getTaskCountInSprint(sprintId: number): Promise<number> {
    return this.get(`/tasks/get-tasks-with-current-sprint/${sprintId}`);
  }

  public getPaginatedSprintList = async ({
    startRow,
    endRow,
    filter,
    sort,
    search,
    startDate,
    endDate,
  }: PaginatedSprintParams): Promise<PaginatedSprintResponse> => {
    const { ApiFilter } = await getSimpleSearchFilterModule();
    const filterAsString = filter?.criteria?.length
      ? // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        ApiFilter.toQueryStringFormat(filter.criteria)
      : undefined;

    const url = constructUrl({
      baseURL: '/sprints',
      params: {
        startDate,
        endDate,
        sort,
        offset: startRow,
        limit: endRow - startRow,
        match: filter.type,
      },
      paramsToAddWithoutKeys: [search, filterAsString],
    });

    return this.get<PaginatedSprintResponse>(url).then((response) => response);
  };

  public async updateSprint(
    sprintId: number,
    payload: SprintUpdatePayload,
    isSync?: boolean,
  ): Promise<Sprint> {
    return this.put(
      `/sprints/${sprintId}${isSync ? `?isSync=${true}` : ''}`,
      payload,
    );
  }

  public async addTasksToSprint(
    sprintId: number,
    payload: number[],
  ): Promise<Sprint> {
    return this.post(`/sprints/${sprintId}/add-tasks`, { taskIds: payload });
  }

  public async getSprintsList({
    startDate,
    endDate,
    projectIds,
    inComplete,
    isAlreadyAddedToPhase = true,
    sprintId,
  }: GetSprintListPayload): Promise<SprintsList> {
    const url = constructUrl({
      baseURL: 'sprints/get-sprints/light/',
      params: {
        sprintId,
        inComplete,
        projectIds,
        startDate: !inComplete ? startDate : undefined,
        endDate: !inComplete ? endDate : undefined,
        isAlreadyAddedToPhase: !isAlreadyAddedToPhase
          ? isAlreadyAddedToPhase
          : undefined,
      },
    });

    return this.get(`${url}`);
  }

  public async updateSprintStatus({ sprintId, payload }): Promise<void> {
    return this.put(`/sprints/${sprintId}`, payload);
  }

  public getTaskStatusesWithCount = async (
    sprintId: number,
  ): Promise<ApiTaskStatusesWithCount[]> => {
    return this.get<ApiTaskStatusesWithCount[]>(
      `sprints/${sprintId}/get-task-statuses`,
    );
  };

  public async removeTaskFromSprint({
    sprintId,
    payload,
  }: SprintRemoveTaskPayload): Promise<void> {
    return this.post(`/sprints/${sprintId}/remove-task`, payload);
  }

  public async getTasksInSprintForExternalUser({
    sprintId,
    projectId,
  }: {
    sprintId: number;
    projectId: number;
  }): Promise<TasksInSprintForExternalUser> {
    return this.get(`projects/${projectId}/sprints/${sprintId}/tasks`);
  }

  public async deleteSprint({ sprintId }: { sprintId: number }): Promise<void> {
    return this.delete(`/sprints/${sprintId}`);
  }

  public async completeSprint({
    sprintId,
    payload,
  }: CompleteSprintPayload): Promise<Sprint> {
    return this.post(`/sprints/${sprintId}/complete-sprint`, payload);
  }

  public async sprintsCount(): Promise<number> {
    return this.get('/sprints/total-count');
  }

  // Epic methods
  public getPaginatedEpicList = async ({
    startRow,
    endRow,
    filter,
    sort,
    search,
    dateRange,
  }: PaginatedEpicParams): Promise<PaginatedEpicResponse> => {
    const { ApiFilter } = await getSimpleSearchFilterModule();
    const filterAsString = filter?.criteria?.length
      ? // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        ApiFilter.toQueryStringFormat(filter.criteria)
      : undefined;

    const url = constructUrl({
      baseURL: '/epics',
      params: {
        ...dateRange,
        sort,
        offset: startRow,
        limit: endRow - startRow,
        match: filter.type,
      },
      paramsToAddWithoutKeys: [search, filterAsString],
    });

    return this.get<PaginatedEpicResponse>(url).then((response) => response);
  };

  public async getEpicsList(): Promise<Epic[]> {
    const data = await this.get<{ data: Epic[] }>(
      '/epics?epic*isArchived.value=false',
    );

    return data.data;
  }

  public async getEpicsListWithGroupBy(
    epicId: number,
    groupBy: string,
    filter: EpicGroupByFilter,
    search?: string,
  ): Promise<Epic[]> {
    const { ApiGroupFilter } = await getSimpleSearchFilterModule();
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(
      filter.criteria,
    );
    const validFilterType = Object.values(QueryCombinerType).includes(
      filter.type,
    )
      ? filter.type
      : QueryCombinerType.All;

    const filterString = filter.criteria.length ? filterAsString : undefined;

    const url = constructUrl({
      baseURL: `/epics/${epicId}/tasks/group`,
      params: {
        groupBy,
        match: validFilterType,
      },
      paramsToAddWithoutKeys: [filterString, search],
    });

    return this.get(url);
  }

  public async createEpic(payload: Epic): Promise<Epic> {
    return this.post('/epics', payload);
  }

  public async getEpic(epicId: number): Promise<Epic> {
    return this.get(`/epics/${epicId}`);
  }

  public async updateEpic(
    epicId: number,
    payload: EpicUpdatePayload,
  ): Promise<Epic> {
    return this.put(`/epics/${epicId}`, payload);
  }

  public async deleteEpic(epicId: number): Promise<void> {
    return this.delete(`/epics/${epicId}`);
  }

  public async archiveEpic(epicId: number): Promise<void> {
    return this.put(`/epics/${epicId}/archive-epic`);
  }

  public async unarchiveEpic(epicId: number): Promise<void> {
    return this.put(`/epics/${epicId}/unarchive-epic`);
  }

  public async addTasksToEpic(
    epicId: number,
    taskIds: number[],
  ): Promise<Epic> {
    return this.post(`/epics/${epicId}/add-tasks`, { taskIds });
  }

  public async removeTaskFromEpic({
    epicId,
    payload,
  }: EpicRemoveTaskPayload): Promise<void> {
    return this.post(`/epics/${epicId}/remove-tasks`, payload);
  }

  public async getEpicsCount(): Promise<number> {
    return this.get('/epics/total-count');
  }

  public async getEpicsForAccount(): Promise<Epic[]> {
    return this.get('/epics/epicsLight');
  }

  // Backlog methods
  public async getBacklogsCount(): Promise<number> {
    return this.get('/backlogs/total-count');
  }

  public async createBacklog(payload: Backlog): Promise<Backlog> {
    return this.post('/backlogs', payload);
  }

  public async updateBacklog(
    backlogId: number,
    payload: Backlog,
  ): Promise<Backlog> {
    return this.put(`/backlogs/${backlogId}`, payload);
  }

  public async getBacklog(backlogId: number): Promise<Backlog> {
    return this.get(`/backlogs/${backlogId}`);
  }

  public async getBacklogTasks({
    backlogId,
    startRow,
    endRow,
    filter,
    filterType,
    sort,
    searchValue,
  }: PaginatedBacklogTasksParams): Promise<PaginatedResponse<Task>> {
    const { ApiGroupFilter } = await getSimpleSearchFilterModule();
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);
    const validFilterType = Object.values(QueryCombinerType).includes(
      filterType,
    )
      ? filterType
      : QueryCombinerType.All;

    const url = constructUrl({
      baseURL: `/backlogs/${backlogId}/tasks`,
      params: {
        sort,
        offset: startRow,
        limit: endRow - startRow,
        match: validFilterType,
      },
      paramsToAddWithoutKeys: [searchValue, filterAsString],
    });

    return this.get<PaginatedResponse<Task>>(url).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  }

  public async postBacklogTasks({
    backlogId,
    startRow,
    endRow,
    filter,
    // filterType,
    sort,
    searchValue,
  }: PaginatedGroupedBacklogTasksParams): Promise<PaginatedResponse<Task>> {
    let url = `/backlogs/${backlogId}/tasks?`;

    if (searchValue) {
      url += `${searchValue}&`;
    }

    if (sort) {
      url += `sort=${sort}`;
    }
    url += `&offset=${startRow}&limit=${endRow - startRow}`;

    return this.post<PaginatedTaskResponse, { filter }>(url, { filter }).then(
      (response) => ({
        ...response,
        data: response.data.map((task) =>
          task.fieldsAccessMeta != null
            ? {
                ...task,
                fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                  task.fieldsAccessMeta,
                ),
              }
            : task,
        ),
      }),
    );
  }

  public async getBacklogSprintTasks({
    backlogId,
    sprintId,
    startRow,
    filterType,
    endRow,
    filter,
    sort,
    searchValue,
  }: PaginatedSprintBacklogTasksParams): Promise<PaginatedResponse<Task>> {
    const { ApiGroupFilter } = await getSimpleSearchFilterModule();
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);
    const validFilterType = Object.values(QueryCombinerType).includes(
      filterType,
    )
      ? filterType
      : QueryCombinerType.All;

    const url = constructUrl({
      baseURL: `/backlogs/${backlogId}/get-sprint-tasks/${sprintId}`,
      params: {
        sort,
        offset: startRow,
        limit: endRow - startRow,
        match: validFilterType,
      },
      paramsToAddWithoutKeys: [searchValue, filterAsString],
    });

    return this.get<PaginatedResponse<Task>>(url).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  }

  public async removeSprintFromBacklog({
    sprintId,
    backlogId,
  }: {
    sprintId: number;
    backlogId: number;
  }): Promise<void> {
    return this.delete(`/backlogs/${backlogId}/sprint/${sprintId}`);
  }

  public async getBacklogSprints(backlogId: number): Promise<Sprint[]> {
    return this.get(`/backlogs/${backlogId}/sprints`);
  }

  public async addSprintToBacklog(
    backlogId: number,
    sprintIds: number[],
  ): Promise<Backlog> {
    return this.post(`/backlogs/${backlogId}/sprints`, sprintIds);
  }

  public async archiveBacklog(backlogId: number): Promise<void> {
    return this.put(`/backlogs/${backlogId}/archive`);
  }

  public async unarchiveBacklog(backlogId: number): Promise<void> {
    return this.put(`/backlogs/${backlogId}/unarchive`);
  }

  public async deleteBacklog({
    backlogId,
  }: {
    backlogId: number;
  }): Promise<void> {
    return this.delete(`/backlogs/${backlogId}`);
  }

  public async getBacklogSprintList(
    backlogId: number,
  ): Promise<ApiBacklogSprintList[]> {
    return this.get(`/backlogs/${backlogId}/available-sprints`);
  }

  public async addTasksToSprintFromBacklog(
    backlogId: number,
    sprintId: number,
    payload: number[],
  ): Promise<Sprint> {
    return this.post(
      `/backlogs/${backlogId}/tasks-to-sprint/${sprintId}`,
      payload,
    );
  }

  public async reorderBacklogTasks(
    backlogId: number,
    beforeTaskId: number,
    taskIds: number[],
  ): Promise<Sprint> {
    return this.put(
      `/backlogs/${backlogId}/tasks-order?beforeTaskId=${beforeTaskId || 0}`,
      taskIds,
    );
  }

  public async checkTaskMovability(
    fromSourceType: string,
    fromSourceId: number,
    toSourceType: string,
    toSourceId: number,
    taskIds: number[],
  ): Promise<CheckTaskMovabilityResponse> {
    return this.post('/backlogs/check-task-movability', {
      fromSourceType,
      fromSourceId,
      toSourceType,
      toSourceId,
      taskIds,
    });
  }

  public async reorderSprintLevelTasks(
    sprintId: number,
    beforeTaskId: number,
    taskIds: number[],
  ): Promise<Sprint> {
    return this.put(
      `/backlogs/sprints/${sprintId}/tasks-order?beforeTaskId=${
        beforeTaskId || 0
      }`,
      taskIds,
    );
  }

  public async moveSprintTasksToBacklogs(
    backlogId: number,
    sprintId: number,
    beforeTaskId: number,
    taskIds: number[],
    extendScope?: boolean,
  ): Promise<Sprint> {
    return this.post(`/backlogs/${backlogId}/move-tasks-from-sprint`, {
      fromEntityType: 'SPRINT',
      fromEntityId: sprintId,
      taskIds,
      beforeTaskId: beforeTaskId || 0,
      extendScope: extendScope || false,
    });
  }

  public async moveBacklogTasksToSprint(
    backlogId: number,
    sprintId: number,
    beforeTaskId: number,
    taskIds: number[],
    extendScope?: boolean,
  ): Promise<Sprint> {
    return this.post(`/backlogs/sprints/${sprintId}/tasks/move-from-backlog`, {
      fromEntityType: 'BACKLOG',
      fromEntityId: backlogId,
      taskIds,
      beforeTaskId: beforeTaskId || 0,
      extendScope: extendScope || false,
    });
  }

  public async moveTasksBetweenSprints(
    fromSprintId: number,
    toSprintId: number,
    beforeTaskId: number,
    taskIds: number[],
    extendScope?: boolean,
  ): Promise<Sprint> {
    return this.post(`/backlogs/${toSprintId}/tasks/move-from-sprint`, {
      fromEntityType: 'SPRINT',
      fromEntityId: fromSprintId,
      taskIds,
      beforeTaskId: beforeTaskId || 0,
      extendScope: extendScope || false,
    });
  }

  public getPaginatedBacklogsList = async ({
    startRow,
    endRow,
    filter,
    sort,
    search,
    dateRange,
  }: PaginatedBacklogParams): Promise<PaginatedBacklogResponse> => {
    const { ApiFilter } = await getSimpleSearchFilterModule();
    const filterAsString = filter?.criteria?.length
      ? // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        ApiFilter.toQueryStringFormat(filter.criteria)
      : undefined;

    const url = constructUrl({
      baseURL: '/backlogs/light',
      params: {
        ...dateRange,
        sort,
        offset: startRow,
        limit: endRow - startRow,
        match: filter.type,
      },
      paramsToAddWithoutKeys: [search, filterAsString],
    });

    return this.get<PaginatedBacklogResponse>(url).then((response) => response);
  };
}

export const SprintPlanningAPI = new SprintPlanningHttpClient();
